import React, { useState, useRef, useEffect } from 'react'
import { Building2, Plus, ChevronDown, Search, Edit3, X } from 'lucide-react'
import { useProvisionedTeams } from '../../lib/redux/provisioned/hooks'
import { Typography } from '../../interfaces/typography'
import { TeamCreationModal } from './TeamCreationModal'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, AppDispatch } from '../../store'
import { updateTeamName } from '../../lib/redux/provisioned/slice'
import { getServicesManager } from '../../services'
import { Modal, Box } from '@mui/material'
import { PrimaryActionButton } from '../../utils/CommonUIComponents'
import { CAPTURE_NOW_TEAM_ID, TRELLUS_TEAM_ID } from '../../cfg/const'

export function TeamProvisioningDropdown() {
  const {
    selectedTeamId,
    selectedTeamName,
    availableTeams,
    isLoading,
    isProvisioning,
    error,
    hasTeams,
    selectTeam,
    initialize,
    dismissError,
    isCreationModalOpen,
    openCreationModal,
    closeCreationModal
  } = useProvisionedTeams()

  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  
  // Team rename states
  const dispatch = useDispatch<AppDispatch>()
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false)
  const [newTeamName, setNewTeamName] = useState<string>('')
  const [renamingTeam, setRenamingTeam] = useState<boolean>(false)
  const [selectedTeamForRename, setSelectedTeamForRename] = useState<any>(null)
  const team = useSelector((state: RootState) => state.team.value)
  const currentUser = useSelector((state: RootState) => state.user.value)
  // User is admin if they belong to CAPTURE_NOW_TEAM_ID or TRELLUS_TEAM_ID
  const isAdmin = currentUser && (currentUser.team_id === CAPTURE_NOW_TEAM_ID || currentUser.team_id === TRELLUS_TEAM_ID)

  // Filter teams based on search query
  const hasMany = availableTeams.length > 30
  const filteredTeams = hasMany && searchQuery.length < 2 
    ? [] 
    : searchQuery.length === 0
      ? availableTeams
      : availableTeams.filter(team => 
          team.team_name.toLowerCase().includes(searchQuery.toLowerCase())
        )

  // Initialize teams on mount
  useEffect(() => {
    initialize()
  }, [initialize])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Close error after a delay
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dismissError()
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [error, dismissError])
  
  // Initialize team name when selected team changes
  useEffect(() => {
    setNewTeamName(selectedTeamForRename?.team_name || '')
  }, [selectedTeamForRename])
  
  const handleRenameTeam = (team: any, event: React.MouseEvent) => {
    event.stopPropagation()
    setSelectedTeamForRename(team)
    setShowRenameModal(true)
  }

  const handleTeamSelect = (teamId: string) => {
    selectTeam(teamId)
    setIsOpen(false)
    setSearchQuery('')
  }

  const handleCreateNewTeam = () => {
    setIsOpen(false)
    setSearchQuery('')
    openCreationModal()
  }

  const handleDropdownToggle = () => {
    setIsOpen(!isOpen)
    if (!isOpen) {
      setSearchQuery('')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <div className="animate-spin w-4 h-4 border border-blue-600 border-t-transparent rounded-full"></div>
        <Typography variant="smallParagraph" color="#6b7280">
          Loading teams...
        </Typography>
      </div>
    )
  }

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* Error Display */}
        {error && (
          <div className="absolute -top-12 left-0 right-0 bg-red-50 border border-red-200 rounded-md px-3 py-2 z-50">
            <Typography variant="smallParagraph" color="#dc2626">
              {error}
            </Typography>
          </div>
        )}

        {/* Main Dropdown Button */}
        <button
          onClick={handleDropdownToggle}
          disabled={isProvisioning}
          className="flex items-center justify-between w-full min-w-[200px] px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-gray-400" />
            <Typography variant="smallParagraph" color={isProvisioning ? "#6b7280" : "#374151"}>
              {isProvisioning ? 'Creating team...' : selectedTeamName}
            </Typography>
          </div>
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </button>
        
        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden flex flex-col">
            {/* Search Input */}
            {hasTeams && (
              <div className="p-2 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={hasMany ? "Type at least 2 characters..." : "Search teams..."}
                    className="w-full pl-8 pr-3 py-1.5 bg-white border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400 text-black"
                  />
                </div>
              </div>
            )}

            {/* Teams List - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              {/* Existing Teams */}
              {hasTeams && (
                <>
                  {filteredTeams.length > 0 ? (
                    filteredTeams.map((team) => (
                      <div
                        key={team.team_id}
                        className="relative flex items-center group"
                      >
                        <button
                          onClick={() => handleTeamSelect(team.team_id)}
                          className="flex-1 px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-none"
                          style={{ color: '#374151' }}
                        >
                          <Typography variant="smallParagraph" color="black">
                            {team.team_name}
                          </Typography>
                        </button>
                        
                        {/* Edit Button - Only show for admins */}
                        {isAdmin && (
                          <button
                            onClick={(e) => handleRenameTeam(team, e)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all duration-200 mr-2"
                            title="Rename team"
                          >
                            <Edit3 size={12} />
                          </button>
                        )}
                      </div>
                    ))
                  ) : searchQuery.length >= 2 || !hasMany ? (
                    <div className="px-3 py-4 text-center">
                      <Typography variant="smallParagraph" color="#9ca3af">
                        No teams found matching "{searchQuery}"
                      </Typography>
                    </div>
                  ) : (
                    <div className="px-3 py-4 text-center">
                      <Typography variant="smallParagraph" color="#9ca3af">
                        Type at least 2 characters to search {availableTeams.length} teams
                      </Typography>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Create New Team Button - Sticky at bottom */}
            <div className="flex-shrink-0 border-t border-gray-100">
              <button
                onClick={handleCreateNewTeam}
                className="flex items-center gap-2 w-full px-3 py-2.5 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-none bg-gray-50"
              >
                <Plus className="w-4 h-4 text-blue-600" />
                <Typography variant="smallParagraph" color="#2563eb" fontWeight="500">
                  Add New Team
                </Typography>
              </button>
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {isProvisioning && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-md">
            <div className="flex items-center gap-2">
              <div className="animate-spin w-4 h-4 border border-blue-600 border-t-transparent rounded-full"></div>
              <Typography variant="smallParagraph" color="#6b7280">
                Creating team...
              </Typography>
            </div>
          </div>
        )}
      </div>

      {/* Team Creation Modal */}
      <TeamCreationModal />
      
      {/* Team Rename Modal */}
      <Modal
        open={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        closeAfterTransition
        sx={{display: 'flex', flexFlow: 'column', alignItems: 'center', justifyContent: 'center'}}
      >
        <Box sx={{
          position: 'relative', maxWidth: '90vw', maxHeight: '90vh', zIndex: 1, minWidth: '450px',
          backgroundColor: 'white', padding: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex', flexFlow: 'column', alignItems: 'stretch', borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Modal Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Edit3 size={16} className="text-blue-600" />
                </div>
                <Typography variant="h4" color="#1F2937" fontWeight={600}>
                  Rename Team
                </Typography>
              </div>
              <button
                onClick={() => setShowRenameModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          {/* Modal Content */}
          <div className="p-6 space-y-4">
            <div>
              <Typography variant="mediumParagraph" color="#374151" fontWeight={500} style={{ marginBottom: '0.5rem' }}>
                Team Name
              </Typography>
              <input
                type="text"
                placeholder="Enter new team name"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                style={{
                  fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#374151'
                }}
              />
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <Typography variant="smallParagraph" color="#6B7280">
                This will update the team name. Current name: <strong>{selectedTeamForRename?.team_name}</strong>
              </Typography>
            </div>
          </div>
          
          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={() => setShowRenameModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              Cancel
            </button>
            <PrimaryActionButton
              icon={renamingTeam ? null : <Edit3 size={16} />}
              onClick={() => {
                if (newTeamName.trim() && newTeamName.trim() !== selectedTeamForRename?.team_name) {
                  setRenamingTeam(true);
                  getServicesManager().updateProvisionTeam(selectedTeamForRename.team_id, newTeamName.trim()).then(() => {
                    setShowRenameModal(false);
                    // Update Redux store with the team name
                    dispatch(updateTeamName({ teamId: selectedTeamForRename.team_id, newName: newTeamName.trim() }));
                  }).finally(() => {
                    setRenamingTeam(false);
                  });
                }
              }}
              disabled={!newTeamName.trim() || newTeamName.trim() === selectedTeamForRename?.team_name || renamingTeam}
            >
              {renamingTeam ? (
                <div className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Renaming...
                </div>
              ) : (
                'Rename Team'
              )}
            </PrimaryActionButton>
          </div>
        </Box>
      </Modal>
    </>
  )
} 